import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { BookOpen, Zap, Trophy, TrendingUp, LogOut, Settings, RefreshCw, Flame, Star, Medal, Users, Layers, Target, User, Home, ChevronLeft, CreditCard, AlertTriangle, CloudOff, Library, Bell, Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import GamificationPanel from '@/components/GamificationPanel';
import NotificationManager from '@/components/NotificationManager';
import WellbeingWidget from '@/components/WellbeingWidget';
import { OposaiLogo } from '@/components/TuPlazaIALogo';
import LanguageSelector from '@/components/LanguageSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [subjects, setSubjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [gamification, setGamification] = useState(null);
  const [failedCount, setFailedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showGamification, setShowGamification] = useState(false);
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    if (user && !user.selected_profession) {
      navigate('/select-profession');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [subjectsRes, statsRes, failedRes, gamificationRes, creditsRes] = await Promise.all([
        axios.get(`${API}/subjects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/failed-questions`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { total_failed: 0 } })),
        axios.get(`${API}/user/gamification`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/credits/balance`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
      ]);
      setSubjects(subjectsRes.data);
      setStats(statsRes.data);
      setFailedCount(failedRes.data.total_failed || 0);
      setGamification(gamificationRes.data);
      setCredits(creditsRes.data);
    } catch (error) {
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.removeItem('session_token');
      navigate('/');
      toast.success(t('dashboard.goodbye'));
    } catch (error) {
      console.error(error);
    }
  };

  const startQuickTest = () => {
    navigate('/exam', { state: { mode: 'quick' } });
  };

  const startSimulacro = () => {
    navigate('/exam', { state: { mode: 'simulacro' } });
  };

  const goToPracticeFailed = () => {
    navigate('/practice-failed');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                title="Ir a inicio"
              >
                <Home className="w-5 h-5 text-slate-600" />
              </button>
              <OposaiLogo size={40} />
            </div>
            <div className="flex items-center gap-4">
              {/* Credits Widget */}
              {credits && (
                <button 
                  onClick={() => navigate('/credits')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${
                    credits.alert?.type === 'critical' 
                      ? 'bg-red-50 hover:bg-red-100' 
                      : credits.alert?.type === 'warning'
                      ? 'bg-amber-50 hover:bg-amber-100'
                      : 'bg-indigo-50 hover:bg-indigo-100'
                  }`}
                >
                  <CreditCard className={`w-4 h-4 ${
                    credits.alert?.type === 'critical' ? 'text-red-500' :
                    credits.alert?.type === 'warning' ? 'text-amber-500' : 'text-indigo-600'
                  }`} />
                  <span className={`text-sm font-bold ${
                    credits.alert?.type === 'critical' ? 'text-red-700' :
                    credits.alert?.type === 'warning' ? 'text-amber-700' : 'text-indigo-700'
                  }`}>
                    {credits.unlimited ? '∞' : credits.credits?.toLocaleString()}
                  </span>
                  {credits.alert && <AlertTriangle className="w-3 h-3 text-amber-500" />}
                </button>
              )}

              {/* Gamification Quick Stats */}
              {gamification && (
                <button 
                  onClick={() => setShowGamification(true)}
                  className="flex items-center gap-3 px-3 py-1.5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-full hover:from-indigo-100 hover:to-purple-100 transition-all"
                >
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-bold text-indigo-700">Nv.{gamification.level}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Flame className="w-4 h-4 text-orange-500" />
                    <span className="text-sm font-bold text-orange-600">{gamification.streak_days}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Medal className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm font-bold text-yellow-600 capitalize">{gamification.league}</span>
                  </div>
                </button>
              )}
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-sm font-bold text-indigo-600">{user?.name?.[0] || 'U'}</span>
                </div>
                <span className="text-sm font-medium text-slate-700">{user?.name || 'Usuario'}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                  {user?.subscription_tier === 'premium' ? 'Premium' : user?.subscription_tier === 'top' ? 'Top' : 'Prueba Gratuita'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/profile')}
                data-testid="profile-nav-btn"
              >
                <User className="w-4 h-4 mr-2" />
                {t('nav.myProfile')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin')}
                data-testid="admin-nav-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Admin
              </Button>
              <LanguageSelector variant="nav" showFlag={true} showName={false} />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('nav.logout', 'Salir')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-extrabold font-heading mb-2">
            {t('dashboard.welcome', { name: user?.name?.split(' ')[0] || 'Usuario' })} 👋
          </h1>
          <p className="text-xl text-slate-600 mb-12">
            {t('dashboard.subtitle')}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm"
            data-testid="stats-exams-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-3xl font-extrabold font-heading">{stats?.total_exams || 0}</p>
                <p className="text-sm text-slate-600">{t('dashboard.examsCompleted')}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm"
            data-testid="stats-score-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-3xl font-extrabold font-heading">
                  {stats?.average_score ? `${stats.average_score}%` : 'N/A'}
                </p>
                <p className="text-sm text-slate-600">{t('dashboard.averageScore')}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm"
            data-testid="stats-hours-card"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-3xl font-extrabold font-heading">{stats?.practice_hours || 0}h</p>
                <p className="text-sm text-slate-600">{t('dashboard.studyTime')}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Risk Metrics */}
        {stats && (stats.total_exams > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm mb-8"
          >
            <h3 className="text-lg font-bold mb-4">{t('dashboard.todayProgress')}</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-1">Probabilidad de Aprobar</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                  stats.pass_probability === 'alta' ? 'bg-emerald-100 text-emerald-700' :
                  stats.pass_probability === 'media' ? 'bg-amber-100 text-amber-700' :
                  'bg-rose-100 text-rose-700'
                }`}>
                  {stats.pass_probability?.toUpperCase()}
                </span>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-1">Riesgo de Suspender</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                  stats.failure_risk === 'bajo' ? 'bg-emerald-100 text-emerald-700' :
                  stats.failure_risk === 'medio' ? 'bg-amber-100 text-amber-700' :
                  'bg-rose-100 text-rose-700'
                }`}>
                  {stats.failure_risk?.toUpperCase()}
                </span>
              </div>
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-1">Preguntas Respondidas</p>
                <span className="inline-block px-3 py-1 rounded-full text-sm font-bold bg-indigo-100 text-indigo-700">
                  {stats.total_questions_answered || 0}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Notification Manager */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.38 }}
          className="mb-8"
        >
          <NotificationManager />
        </motion.div>

        {/* Wellbeing Widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.39 }}
          className="mb-8"
        >
          <WellbeingWidget />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            onClick={startQuickTest}
            className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
            data-testid="quick-test-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
              <Zap className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-extrabold font-heading mb-3">{t('dashboard.quickTest')}</h2>
            <p className="text-lg opacity-90 mb-6">
              Elige asignatura y número de preguntas para una sesión de estudio rápida con feedback instantáneo.
            </p>
            <Button
              className="rounded-full bg-white text-indigo-600 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={startQuickTest}
            >
              Comenzar Test
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            onClick={startSimulacro}
            className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
            data-testid="simulacro-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
              <Trophy className="w-8 h-8" />
            </div>
            <h2 className="text-3xl font-extrabold font-heading mb-3">{t('dashboard.fullSimulacro')}</h2>
            <p className="text-lg opacity-90 mb-6">
              100 preguntas + 10 de reserva (110 total) con temporizador de 150 minutos. Las incorrectas restan. Incluye TODOS los temas.
            </p>
            <Button
              className="rounded-full bg-white text-emerald-600 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={startSimulacro}
            >
              Iniciar Simulacro
            </Button>
          </motion.div>
        </div>

        {failedCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.55 }}
            onClick={goToPracticeFailed}
            className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-12"
            data-testid="practice-failed-card"
          >
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <RefreshCw className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-extrabold font-heading mb-2">{t('dashboard.practiceFailed')}</h2>
                <p className="text-lg opacity-90">
                  Tienes {failedCount} preguntas que necesitas repasar. ¡Mejora tus puntos débiles!
                </p>
              </div>
              <Button
                className="rounded-full bg-white text-amber-600 hover:bg-slate-50 px-6 py-3 font-bold"
                onClick={goToPracticeFailed}
              >
                Repasar Ahora
              </Button>
            </div>
          </motion.div>
        )}

        {/* Community Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.58 }}
          onClick={() => navigate('/community')}
          className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-8"
          data-testid="community-card"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Users className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold font-heading mb-2">Comunidad de Opositores</h2>
              <p className="text-lg opacity-90">
                Comparte dudas, consejos y motivación. Únete a retos grupales y conecta con otros opositores.
              </p>
            </div>
            <Button
              className="rounded-full bg-white text-purple-600 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={() => navigate('/community')}
            >
              Ir a la Comunidad
            </Button>
          </div>
        </motion.div>

        {/* Flashcards Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          onClick={() => navigate('/flashcards')}
          className="bg-gradient-to-br from-cyan-600 to-blue-600 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-8"
          data-testid="flashcards-card"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Layers className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold font-heading mb-2">Flashcards FSRS</h2>
              <p className="text-lg opacity-90">
                Memoriza síndromes, dosis, valores normales y fórmulas con repetición espaciada científica.
              </p>
            </div>
            <Button
              className="rounded-full bg-white text-cyan-600 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={() => navigate('/flashcards')}
            >
              Estudiar Flashcards
            </Button>
          </div>
        </motion.div>

        {/* Study Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.61 }}
          onClick={() => navigate('/study-plan')}
          className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-8"
          data-testid="study-plan-card"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold font-heading mb-2">Plan de Estudio IA</h2>
              <p className="text-lg opacity-90">
                Predicción ML de resultados, plan personalizado y detección de burnout.
              </p>
            </div>
            <Button
              className="rounded-full bg-white text-amber-600 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={() => navigate('/study-plan')}
            >
              Ver mi Plan
            </Button>
          </div>
        </motion.div>

        {/* Offline Tests Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.64 }}
          onClick={() => navigate('/offline-test')}
          className="bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-8"
          data-testid="offline-test-card"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <CloudOff className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold font-heading mb-2">Tests Offline</h2>
              <p className="text-lg opacity-90">
                Descarga preguntas y practica sin conexión a internet. Ideal para estudiar en cualquier lugar.
              </p>
            </div>
            <Button
              className="rounded-full bg-white text-slate-800 hover:bg-slate-50 px-6 py-3 font-bold"
              onClick={() => navigate('/offline-test')}
            >
              Ir a Offline
            </Button>
          </div>
        </motion.div>

        {/* Library Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.68 }}
          onClick={() => navigate('/library')}
          className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-8 text-white cursor-pointer hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 mb-8"
          data-testid="library-card"
        >
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <BookOpen className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-extrabold font-heading mb-2">Biblioteca</h2>
              <p className="text-lg opacity-90">
                Audios, PDFs y material de estudio. Todo el contenido disponible para descargar y usar offline.
              </p>
            </div>
            <Button
              className="rounded-full bg-white text-indigo-700 hover:bg-indigo-50 px-6 py-3 font-bold"
              onClick={() => navigate('/library')}
            >
              Ver Biblioteca
            </Button>
          </div>
        </motion.div>

        {(user?.subscription_tier === 'trial' || !user?.subscription_tier || user?.subscription_tier === 'expired') && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 p-8 text-center"
            data-testid="upgrade-banner"
          >
            <h3 className="text-2xl font-bold font-heading text-slate-900 mb-3">
              ¡Estás en tu prueba gratuita de 7 días!
            </h3>
            <p className="text-slate-700 mb-6">
              Accede a todas las funciones del Plan Top. Actualiza a Premium para análisis avanzado y soporte prioritario.
            </p>
            <Button
              className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 font-bold"
              onClick={() => navigate('/pricing')}
              data-testid="upgrade-btn"
            >
              Ver Planes
            </Button>
          </motion.div>
        )}
      </div>
      
      {/* Gamification Panel */}
      <GamificationPanel 
        isOpen={showGamification} 
        onClose={() => setShowGamification(false)} 
      />
    </div>
  );
};

export default Dashboard;
